<template>
  <div
      id="weather-widget"
      class="bg-white/98 dark:bg-gray-800/95 rounded-lg widget-shadow overflow-hidden text-gray-800 dark:text-gray-100 max-w-[300px] transition-all duration-500"
  >
    <!-- 初始定位状态 -->
    <div id="locating" v-if="isLocating && !errorMessage" class="p-4 flex flex-col items-center justify-center">
      <div class="w-6 h-6 border-2 border-primary border-t-transparent rounded-full spin mb-2"></div>
      <div class="text-xs text-gray-500 dark:text-gray-400">正在获取您的位置...</div>
    </div>

    <!-- 加载天气状态 -->
    <div id="loading-weather" v-if="isLoading && !errorMessage" class="hidden p-4 flex flex-col items-center justify-center">
      <div class="w-6 h-6 border-2 border-primary border-t-transparent rounded-full spin mb-2"></div>
      <div class="text-xs text-gray-500 dark:text-gray-400">正在获取天气数据...</div>
    </div>

    <!-- 天气信息 -->
    <div id="weather-content" v-if="weatherData && !isLocating && !isLoading && !errorMessage" class="hidden">
      <!-- 主要天气信息 -->
      <div class="p-3 flex items-center justify-between border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center">
          <div :class="['weather-icon text-2xl mr-2', iconAnimationClass]">
            <i :class="weatherIconClass"></i>
          </div>
          <div>
            <div class="text-lg font-bold" id="temperature">{{ weatherData.temp }}°</div>
            <div class="text-xs text-gray-500 dark:text-gray-400 capitalize" id="condition">{{ weatherData.condition }}</div>
          </div>
        </div>
        <div class="text-right">
          <div class="font-medium" id="city">{{ weatherData.city }}</div>
          <div class="text-xs text-gray-500 dark:text-gray-400" id="update-time">{{ updateTime }}</div>
        </div>
      </div>

      <!-- 详细信息 -->
      <div class="p-3 grid grid-cols-2 gap-2 border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center text-xs">
          <i class="fa fa-tint text-rainy mr-1"></i>
          <span>湿度: <span id="humidity">{{ weatherData.humidity }}%</span></span>
        </div>
        <div class="flex items-center text-xs">
          <i class="fa fa-wind text-cloudy mr-1"></i>
          <span>风速: <span id="wind-speed">{{ weatherData.windSpeed }} km/h</span></span>
        </div>
        <div class="flex items-center text-xs">
          <i class="fa fa-location-arrow text-primary mr-1"></i>
          <span>风向: <span id="wind-direction">{{ weatherData.windDirection }}</span></span>
        </div>
        <div class="flex items-center text-xs">
          <i class="fa fa-eye text-foggy mr-1"></i>
          <span>能见度: <span id="visibility">{{ weatherData.visibility }} km</span></span>
        </div>
      </div>

      <!-- 未来预报 -->
      <div class="p-2">
        <div class="text-xs font-medium mb-2 text-gray-500 dark:text-gray-400">未来预报</div>
        <div id="forecast-container" class="flex justify-between text-center">
          <div
              v-for="(day, index) in weatherData.forecast"
              :key="index"
              class="forecast-item"
              :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="text-xs text-gray-500 dark:text-gray-400">{{ day.weekday }}</div>
            <div class="text-xs my-1">
              <i :class="`fa ${getWeatherIcon(day.condition).icon} ${getWeatherIcon(day.condition).color}`"></i>
            </div>
            <div class="text-xs font-medium">{{ day.temp }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 错误状态 -->
    <div id="error" v-if="errorMessage" class="hidden p-4">
      <div class="text-sm text-red-400 flex items-center">
        <i class="fa fa-exclamation-circle mr-1"></i>
        <span id="error-message">{{ errorMessage }}</span>
      </div>
      <button
          @click="startLocation"
          class="mt-2 text-xs text-primary hover:underline"
      >
        重试
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmbeddedWeather',
  data() {
    return {
      // OpenWeatherMap API密钥，请替换为您自己的
      OPENWEATHER_API_KEY: '您的OpenWeatherMap_API密钥',

      // 天气数据
      weatherData: null,
      updateTime: '',

      // 状态控制
      isLocating: true,
      isLoading: false,
      errorMessage: '',

      // 天气图标和动画
      weatherIcon: {
        icon: 'fa-sun-o',
        color: 'text-sunny'
      },
      iconAnimation: 'sun-animation'
    }
  },

  computed: {
    // 天气图标样式
    weatherIconClass() {
      return 'fa ' + this.weatherIcon.icon + ' ' + this.weatherIcon.color;
    },

    // 图标动画样式
    iconAnimationClass() {
      return this.iconAnimation;
    }
  },

  mounted() {
    // 初始化时开始定位
    this.startLocation();
  },

  methods: {
    // 开始定位流程
    startLocation() {
      // 重置状态和样式
      this.weatherData = null;
      this.errorMessage = '';
      this.isLocating = true;
      this.isLoading = false;

      // 隐藏所有内容区域，显示定位状态
      this.$nextTick(function () {
        var weatherContent = document.querySelector('#weather-content');
        if (weatherContent) {
          weatherContent.classList.add('hidden');
        }

        var loadingWeather = document.querySelector('#loading-weather');
        if (loadingWeather) {
          loadingWeather.classList.add('hidden');
        }

        var errorEl = document.querySelector('#error');
        if (errorEl) {
          errorEl.classList.add('hidden');
        }

        var locatingEl = document.querySelector('#locating');
        if (locatingEl) {
          locatingEl.classList.remove('hidden');
        }
      });

      // 直接通过IP获取位置，不使用浏览器定位
      this.getLocationByIP();
    },

    // 使用IP定位服务获取大致位置
    getLocationByIP: async function () {
      try {
        // 使用第三方IP定位服务
        var response = await fetch('https://ipapi.co/json/');
        if (!response.ok) {
          throw new Error('IP定位服务响应失败');
        }

        var data = await response.json();

        if (data.city && data.latitude && data.longitude) {
          // 获取到位置后，获取天气数据
          this.fetchWeatherDataByCoordinates(data.latitude, data.longitude, data.city);
        } else {
          throw new Error('无法通过IP获取位置信息');
        }
      } catch (err) {
        console.error('IP定位失败:', err);
        this.errorMessage = '无法获取您的位置，请稍后重试';
        this.isLocating = false;

        // 更新UI显示错误状态
        this.$nextTick(function () {
          var locatingEl = document.querySelector('#locating');
          if (locatingEl) {
            locatingEl.classList.add('hidden');
          }

          var errorEl = document.querySelector('#error');
          if (errorEl) {
            errorEl.classList.remove('hidden');
          }
        });
      }
    },

    // 根据经纬度获取天气数据
    fetchWeatherDataByCoordinates: async function (lat, lon, city) {
      try {
        // 更新状态为加载中
        this.isLocating = false;
        this.isLoading = true;

        this.$nextTick(function () {
          var locatingEl = document.querySelector('#locating');
          if (locatingEl) {
            locatingEl.classList.add('hidden');
          }

          var loadingWeather = document.querySelector('#loading-weather');
          if (loadingWeather) {
            loadingWeather.classList.remove('hidden');
          }
        });

        // 调用OpenWeatherMap当前天气API
        var currentWeatherUrl = 'https://api.openweathermap.org/data/2.5/weather?lat=' + lat + '&lon=' + lon + '&appid=' + this.OPENWEATHER_API_KEY + '&units=metric&lang=zh_cn';
        var forecastUrl = 'https://api.openweathermap.org/data/2.5/forecast?lat=' + lat + '&lon=' + lon + '&appid=' + this.OPENWEATHER_API_KEY + '&units=metric&lang=zh_cn&cnt=16';

        // 并行请求当前天气和预报
        var responses = await Promise.all([
          fetch(currentWeatherUrl),
          fetch(forecastUrl)
        ]);
        var currentResponse = responses[0];
        var forecastResponse = responses[1];

        if (!currentResponse.ok || !forecastResponse.ok) {
          throw new Error('天气数据请求失败');
        }

        var currentData = await currentResponse.json();
        var forecastData = await forecastResponse.json();

        // 处理和格式化数据
        this.processWeatherData(currentData, forecastData, city);

      } catch (err) {
        console.error('获取天气数据失败:', err);
        this.errorMessage = '无法获取天气数据，请稍后重试';
        this.isLoading = false;

        this.$nextTick(function () {
          var loadingWeather = document.querySelector('#loading-weather');
          if (loadingWeather) {
            loadingWeather.classList.add('hidden');
          }

          var errorEl = document.querySelector('#error');
          if (errorEl) {
            errorEl.classList.remove('hidden');
          }
        });
      }
    },

    // 处理天气数据
    processWeatherData: function (currentData, forecastData, city) {
      // 转换风向角度为方向文字
      var windDirection = this.getWindDirection(currentData.wind.deg);

      // 处理预报数据，每天取一个数据点
      var dailyForecasts = {};
      forecastData.list.forEach(function (item) {
        var date = new Date(item.dt * 1000).toLocaleDateString();
        // 只保留每天的第一个预报
        if (!dailyForecasts[date]) {
          dailyForecasts[date] = item;
        }
      });

      // 转换为数组并取未来4天
      var forecastList = Object.values(dailyForecasts).slice(1, 5).map(function (item) {
        return {
          weekday: this.getWeekday(item.dt * 1000),
          temp: Math.round(item.main.temp) + '°',
          condition: item.weather[0].description
        };
      }.bind(this));

      // 整理天气数据
      this.weatherData = {
        city: city || currentData.name,
        temp: Math.round(currentData.main.temp),
        condition: currentData.weather[0].description,
        humidity: currentData.main.humidity,
        windSpeed: (currentData.wind.speed * 3.6).toFixed(1), // 转换为km/h
        windDirection: windDirection,
        visibility: (currentData.visibility / 1000).toFixed(1), // 转换为km
        forecast: forecastList
      };

      // 更新更新时间
      this.updateTime = this.formatTime(new Date());

      // 设置天气图标和样式
      this.setWeatherIconAndStyle(currentData.weather[0].description);

      // 更新UI
      this.isLoading = false;

      this.$nextTick(function () {
        var loadingWeather = document.querySelector('#loading-weather');
        if (loadingWeather) {
          loadingWeather.classList.add('hidden');
        }

        var weatherContent = document.querySelector('#weather-content');
        if (weatherContent) {
          weatherContent.classList.remove('hidden');
        }
      });
    },

    // 获取风向文字描述
    getWindDirection: function (degrees) {
      var directions = ['北风', '东北风', '东风', '东南风', '南风', '西南风', '西风', '西北风'];
      var index = Math.round(degrees / 45) % 8;
      return directions[index];
    },

    // 获取天气图标和颜色
    getWeatherIcon: function (condition) {
      condition = condition.toLowerCase();

      if (condition.includes('晴') || condition.includes('clear')) {
        return {icon: 'fa-sun-o', color: 'text-sunny'};
      } else if (condition.includes('云') || condition.includes('cloud')) {
        return {icon: 'fa-cloud', color: 'text-cloudy'};
      } else if (condition.includes('雨') || condition.includes('rain')) {
        return {icon: 'fa-tint', color: 'text-rainy'};
      } else if (condition.includes('雪') || condition.includes('snow')) {
        return {icon: 'fa-snowflake-o', color: 'text-snowy'};
      } else if (condition.includes('雷') || condition.includes('thunder')) {
        return {icon: 'fa-bolt', color: 'text-stormy'};
      } else if (condition.includes('雾') || condition.includes('fog') ||
          condition.includes('霾') || condition.includes('haze')) {
        return {icon: 'fa-smog', color: 'text-foggy'};
      }

      return {icon: 'fa-question', color: 'text-primary'};
    },

    // 设置天气图标和样式
    setWeatherIconAndStyle: function (condition) {
      var weatherIcon = this.getWeatherIcon(condition);
      this.weatherIcon = weatherIcon;

      // 重置所有动画类
      this.iconAnimation = '';

      // 设置相应的动画
      condition = condition.toLowerCase();
      if (condition.includes('晴') || condition.includes('clear')) {
        this.iconAnimation = 'sun-animation';
      } else if (condition.includes('雨') || condition.includes('rain')) {
        this.iconAnimation = 'rain-animation';
      } else if (condition.includes('雪') || condition.includes('snow')) {
        this.iconAnimation = 'snow-animation';
      }

      // 更新背景样式
      this.$nextTick(function () {
        var weatherWidget = document.getElementById('weather-widget');
        if (weatherWidget) {
          // 移除所有天气背景类
          weatherWidget.classList.remove(
              'weather-bg-sunny',
              'weather-bg-cloudy',
              'weather-bg-rainy',
              'weather-bg-snowy',
              'weather-bg-stormy',
              'weather-bg-foggy'
          );

          // 设置新的背景类
          if (condition.includes('晴') || condition.includes('clear')) {
            weatherWidget.classList.add('weather-bg-sunny');
          } else if (condition.includes('云') || condition.includes('cloud')) {
            weatherWidget.classList.add('weather-bg-cloudy');
          } else if (condition.includes('雨') || condition.includes('rain')) {
            weatherWidget.classList.add('weather-bg-rainy');
          } else if (condition.includes('雪') || condition.includes('snow')) {
            weatherWidget.classList.add('weather-bg-snowy');
          } else if (condition.includes('雷') || condition.includes('thunder')) {
            weatherWidget.classList.add('weather-bg-stormy');
          } else if (condition.includes('雾') || condition.includes('fog') ||
              condition.includes('霾') || condition.includes('haze')) {
            weatherWidget.classList.add('weather-bg-foggy');
          }
        }
      });
    },

    // 格式化时间
    formatTime: function (date) {
      var hours = String(date.getHours()).padStart(2, '0');
      var minutes = String(date.getMinutes()).padStart(2, '0');
      return hours + ':' + minutes + '更新';
    },

    // 获取星期几
    getWeekday: function (timestamp) {
      var weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
      var date = new Date(timestamp);
      return weekdays[date.getDay()];
    }
  }
}
</script>

<style scoped>
/*@import url('https://cdn.jsdelivr.net/npm/font-awesome@4.7.0/css/font-awesome.min.css');*/

/* 基础样式 */
#weather-widget {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* 颜色定义 */
.text-sunny {
  color: #FFD740;
}

.text-cloudy {
  color: #90CAF9;
}

.text-rainy {
  color: #64B5F6;
}

.text-snowy {
  color: #B3E5FC;
}

.text-stormy {
  color: #90CAF9;
}

.text-foggy {
  color: #E0E0E0;
}

.text-primary {
  color: #64B5F6;
}

.border-primary {
  border-color: #64B5F6;
}

/* 背景样式 */
.weather-bg-sunny {
  background: linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%);
}

.weather-bg-cloudy {
  background: linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%);
}

.weather-bg-rainy {
  background: linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%);
}

.weather-bg-snowy {
  background: linear-gradient(135deg, #F5F7FA 0%, #E4EAF1 100%);
}

.weather-bg-stormy {
  background: linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%);
}

.weather-bg-foggy {
  background: linear-gradient(135deg, #FAFAFA 0%, #F5F5F5 100%);
}

/* 动画效果 */
.weather-icon {
  transition: transform 0.3s ease;
}

.weather-icon:hover {
  transform: scale(1.1);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.forecast-item {
  animation: fadeIn 0.5s ease forwards;
  opacity: 0;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spin {
  animation: spin 1s linear infinite;
}

/* 雨滴动画 */
@keyframes raindrop {
  0% {
    transform: translateY(-10px) opacity(0);
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateY(10px) opacity(0);
  }
}

.rain-animation {
  animation: raindrop 1s linear infinite;
}

/* 雪花动画 */
@keyframes snowflake {
  0% {
    transform: translateY(-10px) rotate(0deg) opacity(0);
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateY(10px) rotate(360deg) opacity(0);
  }
}

.snow-animation {
  animation: snowflake 2s linear infinite;
}

/* 阳光动画 */
@keyframes sunShine {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.sun-animation {
  animation: sunShine 15s linear infinite;
}

/* 布局样式 */
.widget-shadow {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.hidden {
  display: none !important;
}

.flex {
  display: flex;
}

.flex-col {
  flex-direction: column;
}

.items-center {
  align-items: center;
}

.justify-center {
  justify-content: center;
}

.justify-between {
  justify-content: space-between;
}

.grid {
  display: grid;
}

.grid-cols-2 {
  grid-template-columns: repeat(2, 1fr);
}

.gap-2 {
  gap: 0.5rem;
}

.p-2 {
  padding: 0.5rem;
}

.p-3 {
  padding: 0.75rem;
}

.p-4 {
  padding: 1rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.text-xs {
  font-size: 0.75rem;
}

.text-sm {
  font-size: 0.875rem;
}

.text-lg {
  font-size: 1.125rem;
}

.text-2xl {
  font-size: 1.5rem;
}

.font-bold {
  font-weight: 700;
}

.font-medium {
  font-weight: 500;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.rounded-full {
  border-radius: 9999px;
}

.border {
  border-width: 1px;
  border-style: solid;
}

.border-gray-100 {
  border-color: #f3f4f6;
}

.border-t-transparent {
  border-top-color: transparent;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.capitalize {
  text-transform: capitalize;
}

.overflow-hidden {
  overflow: hidden;
}

.hover\:underline:hover {
  text-decoration: underline;
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.duration-500 {
  transition-duration: 500ms;
}

/* 深色模式样式 */
.dark\:bg-gray-800\/95 {
  background-color: rgba(31, 41, 55, 0.95);
}

.dark\:text-gray-100 {
  color: #f3f4f6;
}

.dark\:text-gray-400 {
  color: #9ca3af;
}

.dark\:border-gray-700 {
  border-color: #374151;
}
</style>



// WEBPACK FOOTER //
// src/pages/city/EmbeddedWeather.vue